/**
 * HALO - Site Header
 * Top bar with sidebar toggle, page title, and action buttons.
 */

import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { PanelLeftClose, PanelLeft, Bell, RefreshCw } from "lucide-react";
import { useSidebar } from "@/components/layout/AppSidebar";
import { Separator } from "@/components/ui/separator";
import { ThemeToggleSimple } from "@/components/ThemeToggle";
import { cn } from "@/lib/utils";

// Map routes to page titles
const routeTitles: Record<string, string> = {
  "/": "Dashboard",
  "/team-pulse": "Team Pulse",
  "/admin": "Admin Dashboard",
  "/ai-analysis": "AI Analysis",
  "/export": "Export",
  "/settings": "Settings",
  "/help": "Help & Support",
};

interface SiteHeaderProps {
  alertCount?: number;
  onAlertClick?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function SiteHeader({
  alertCount = 0,
  onAlertClick,
  onRefresh,
  isRefreshing = false,
}: SiteHeaderProps) {
  const { collapsed, toggle } = useSidebar();
  const location = useLocation();

  const pageTitle = routeTitles[location.pathname] || "HALO";

  return (
    <header className="flex h-[var(--header-height)] shrink-0 items-center gap-2 border-b border-glass-grey bg-surface-dark sticky top-0 z-20">
      <div className="flex w-full items-center gap-2 px-4 lg:px-6">
        {/* Sidebar toggle */}
        <button
          onClick={toggle}
          className="p-2 -ml-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-foreground"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeft className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </button>

        <Separator orientation="vertical" className="mx-2 h-4" />

        {/* Page title */}
        <h1 className="text-base font-medium text-foreground">{pageTitle}</h1>

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-2">
          {/* Refresh */}
          {onRefresh && (
            <motion.button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-foreground disabled:opacity-40"
              whileTap={{ scale: 0.95 }}
              title="Refresh"
            >
              <RefreshCw
                className={cn("w-4.5 h-4.5", isRefreshing && "animate-spin")}
              />
            </motion.button>
          )}

          {/* Alerts */}
          {onAlertClick && (
            <motion.button
              onClick={onAlertClick}
              className="relative p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-foreground"
              whileTap={{ scale: 0.95 }}
              title="Alerts"
            >
              <Bell className="w-4.5 h-4.5" />
                {alertCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-signal-crimson text-foreground text-[10px] font-medium rounded-full flex items-center justify-center">
                  {alertCount > 9 ? "9+" : alertCount}
                </span>
              )}
            </motion.button>
          )}

          {/* Theme Toggle */}
          <ThemeToggleSimple />
        </div>
      </div>
    </header>
  );
}
